import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { GLOBAL_IMAGES } from "../assets/asset";
import { useSubscription } from "../context/SubscriptionContext";
import GlobalSubscriptionBanner from "../components/subscription/GlobalSubscriptionBanner";
import { logout } from "../features/auth/user.reducer";
import {
  startJourney,
  logPricingPageViewed,
  logPlanSelected,
  logStripeCheckoutSessionCreateResult,
  logStripeRedirectStarted,
  logStripeReturnResult,
  logPaymentJourneyFinalStatus,
  getCurrentSessionId,
  getCurrentJourneyId,
} from "../utils/datadogLogger";
import { useSelector } from "react-redux";
import { getSubscriptionPlanKey } from "../utils/subscriptionPlan";
import { isParentSessionMode } from "../features/parentFlow/parentFlow";

const planCards = [
  {
    name: "Monthly Basic",
    planType: "basic",
    interval: "monthly",
    currency: "AUD",
    description:
      "A monthly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Flexibility",
      "Short-Term Learning Goals",
      "Budget Constraints",
      "Appraisal Period",
    ],
    price: "A$4.99",
    billingText: "/ month",
  },
  {
    name: "Monthly Pro",
    planType: "pro",
    interval: "monthly",
    currency: "AUD",
    description:
      "A monthly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Flexibility",
      "Short-Term Learning Goals",
      "Budget Constraints",
      "Appraisal Period",
    ],
    price: "A$9.99",
    billingText: "/ month",
  },
  {
    name: "Yearly Basic",
    planType: "basic",
    interval: "yearly",
    currency: "AUD",
    description:
      "A yearly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Cost Efficiency",
      "Long-Term Commitment",
      "Reduced Hassle",
      "Consistent Learning",
    ],
    price: "A$59.99",
    billingText: "/ year",
  },
  {
    name: "Yearly Pro",
    planType: "pro",
    interval: "yearly",
    currency: "AUD",
    description:
      "A yearly subscription is an ideal choice for you in various situations:",
    benefits: [
      "Cost Efficiency",
      "Long-Term Commitment",
      "Reduced Hassle",
      "Consistent Learning",
    ],
    price: "A$119.99",
    billingText: "/ year",
  },
];

const normalizeSubscriptionStatus = (subscription = {}) => {
  const normalizedStatus = String(
    subscription?.subscription_status || subscription?.status || ""
  )
    .trim()
    .toLowerCase();

  if (normalizedStatus === "cancelled") {
    return "canceled";
  }

  return normalizedStatus;
};

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);
  const isParentMode = isParentSessionMode();
  const hasHandledReturnRef = useRef(false);

  const [activeButtonKey, setActiveButtonKey] = useState("");
  const [frontendError, setFrontendError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  // Log pricing page viewed on mount
  useEffect(() => {
    startJourney('purchase');
    const availablePlans = planCards.map(p => ({
      name: p.name,
      planType: p.planType,
      interval: p.interval,
      price: p.price,
    }));
    logPricingPageViewed();
  }, []);

  const redirectSearchParam = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("redirect") || "";
  }, [location.search]);
  const redirectPath =
    location?.state?.from || redirectSearchParam || "/yearsdashboard";
  const shouldAutoRedirect = Boolean(location?.state?.from || redirectSearchParam);
  const isReturnFromCheckout = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return (
      ["success", "redirect_status", "session_id", "setup_intent"].some((key) =>
        searchParams.has(key)
      ) || ["success", "cancel"].includes(searchParams.get("subscription"))
    );
  }, [location.search]);

  const {
    subscription,
    hasAccess,
    isChecking,
    checkSubscription,
    createSubscription,
    isCreating,
    normalizedPlanType,
  } = useSubscription();
  const subscriptionStatus = useMemo(
    () => normalizeSubscriptionStatus(subscription),
    [subscription]
  );
  const requiresPaymentMethod = Boolean(subscription?.requires_payment_method);
  const hasCurrentSubscription =
    subscriptionStatus !== "canceled" &&
    (Boolean(subscription?.has_access) ||
      ((subscriptionStatus === "active" || subscriptionStatus === "trialing") &&
        !requiresPaymentMethod));

  const trialText = useMemo(() => {
    if (subscriptionStatus === "trialing") {
      const daysLeft = Number(subscription?.days_left || 0);
      return `Trial active, ${daysLeft} days left`;
    }
    if (subscriptionStatus === "active") {
      return "Subscription active";
    }
    if (subscriptionStatus === "canceled") {
      return "Subscription cancelled";
    }
    return "Subscription required";
  }, [subscription, subscriptionStatus]);

  const currentPlanKey = useMemo(() => {
    if (!hasCurrentSubscription) {
      return "";
    }

    return getSubscriptionPlanKey(subscription, user) ||
      getSubscriptionPlanKey(
        { plan_type: normalizedPlanType },
        subscription,
        user
      );
  }, [hasCurrentSubscription, normalizedPlanType, subscription, user]);

  const resetCheckoutUiState = useCallback(() => {
    setIsRedirecting(false);
    setActiveButtonKey("");
    try {
      sessionStorage.removeItem("pendingStripeCheckout");
      sessionStorage.removeItem("pendingStripeCheckoutAt");
    } catch (_) {
      // Ignore storage errors.
    }
  }, []);

  useEffect(() => {
    if (hasAccess && shouldAutoRedirect) {
      navigate(redirectPath, { replace: true });
    }
  }, [hasAccess, shouldAutoRedirect, navigate, redirectPath]);

  useEffect(() => {
    if (isCreating) return;
    if (!isReturnFromCheckout) {
      resetCheckoutUiState();
    }
  }, [isCreating, isReturnFromCheckout, resetCheckoutUiState]);

  useEffect(() => {
    const handlePageShow = () => {
      if (!isReturnFromCheckout && !isCreating) {
        resetCheckoutUiState();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isReturnFromCheckout, isCreating, resetCheckoutUiState]);

  useEffect(() => {
    if (hasHandledReturnRef.current) return;

    if (!isReturnFromCheckout) return;

    hasHandledReturnRef.current = true;
    let cancelled = false;

    const searchParams = new URLSearchParams(location.search);
    const returnResult = searchParams.get("subscription") === "success" ? "success" : 
                        searchParams.get("subscription") === "cancel" ? "cancelled" : "unknown";
    const sessionId = searchParams.get("session_id");

    // Log Stripe return result
    logStripeReturnResult(
      user?.email || null,
      returnResult,
      sessionId,
      Object.fromEntries(searchParams)
    );

    const runPostCheckoutSync = async () => {
      const maxAttempts = 8;
      const delayMs = 1500;
      setIsPolling(true);

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) return;

        try {
          const latestData = await checkSubscription();
          const latestStatus = latestData?.subscription_status;
          const latestRequiresPaymentMethod = Boolean(
            latestData?.requires_payment_method
          );
          const canAccessApp =
            Boolean(latestData?.has_access) ||
            ((latestStatus === "active" || latestStatus === "trialing") &&
              !latestRequiresPaymentMethod);

          if (canAccessApp) {
            // Log successful payment journey
            logPaymentJourneyFinalStatus(
              user?.email || null,
              'success',
              null,
              null,
              sessionId,
              latestData?.stripe_subscription_id
            );

            navigate(redirectPath, { replace: true });
            return;
          }
        } catch (_) {
          // Retry for transient webhook delays.
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      setIsPolling(false);
    };

    runPostCheckoutSync();

    return () => {
      cancelled = true;
      setIsPolling(false);
    };
  }, [isReturnFromCheckout, checkSubscription, navigate, redirectPath, location.search]);

  const handleGetStarted = async (planType, interval) => {
    if (isRedirecting) return;

    const activePlanKey = `${interval}_${planType}`;
    setActiveButtonKey(activePlanKey);
    setFrontendError("");

    // Log plan selected
    const selectedPlan = planCards.find(
      p => p.planType === planType && p.interval === interval
    );
    if (selectedPlan) {
      logPlanSelected(
        user?.email || null,
        activePlanKey,
        selectedPlan.name,
        interval,
        selectedPlan.price
      );
    }

    setIsRedirecting(true);
    try {
      sessionStorage.setItem("pendingStripeCheckout", activePlanKey);
      sessionStorage.setItem("pendingStripeCheckoutAt", String(Date.now()));
    } catch (_) {
      // Non-blocking: continue redirect even if storage is unavailable.
    }

    try {
      const subscriptionReturnUrl = `${window.location.origin}/subscription`;
      const successUrl = `${subscriptionReturnUrl}?subscription=success&redirect=${encodeURIComponent(
        redirectPath
      )}`;
      const cancelUrl = `${subscriptionReturnUrl}?subscription=cancel&redirect=${encodeURIComponent(
        redirectPath
      )}`;
      const checkoutData = await createSubscription(planType, interval, {
        success_url: successUrl,
        cancel_url: cancelUrl,
      });

      // Log checkout session creation result
      logStripeCheckoutSessionCreateResult(
        user?.email || null,
        activePlanKey,
        checkoutData?.status || 200,
        checkoutData?.checkout_session_id || checkoutData?.session_id,
        { plan_type: planType, interval },
        { success: true },
        null
      );

      const checkoutRedirectUrl =
        checkoutData?.checkout_url ||
        checkoutData?.checkoutUrl ||
        "";

      if (checkoutRedirectUrl) {
        // Log Stripe redirect started
        logStripeRedirectStarted(
          user?.email || null,
          activePlanKey,
          checkoutData?.checkout_session_id || checkoutData?.session_id
        );

        window.location.assign(checkoutRedirectUrl);
        return;
      }

      throw new Error("Missing checkout_url in checkout session response.");
    } catch (error) {
      setIsRedirecting(false);
      setActiveButtonKey("");
      const errorMsg = error?.response?.data?.message || error?.message || "Unable to start checkout. Please try again.";
      setFrontendError(errorMsg);

      // Log checkout session creation error
      logStripeCheckoutSessionCreateResult(
        user?.email || null,
        activePlanKey,
        error?.response?.status || 500,
        null,
        { plan_type: planType, interval },
        error?.response?.data || {},
        errorMsg
      );

      // Log payment journey final status with failure
      logPaymentJourneyFinalStatus(
        user?.email || null,
        'failed',
        'checkout_session_creation',
        activePlanKey,
        null,
        null
      );
    }
  };

  const handleLogout = () => {
    setShowLogoutPopup(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutPopup(false);
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const outerContainerClassName = isParentMode
    ? "relative min-h-screen overflow-hidden bg-[#f8f3ea] font-mali px-4 py-4 md:py-6"
    : "min-h-screen font-mali flex items-center justify-center overflow-y-auto px-4 py-4 md:py-6";
  const outerContainerStyle = isParentMode
    ? undefined
    : {
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
  const panelClassName = isParentMode
    ? "relative z-10 mx-auto w-full max-w-7xl rounded-[36px] border border-[#e5dacb] bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(255,247,239,0.93)_100%)] px-4 pb-6 pt-5 shadow-[0_28px_70px_rgba(141,107,70,0.16)] backdrop-blur-sm sm:px-6 md:px-8 lg:px-10"
    : "bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg w-full max-w-7xl mx-auto px-4 pt-4 pb-5 sm:px-6 md:px-8 lg:px-10 relative max-h-[calc(100vh-1rem)] md:max-h-[calc(100vh-1.5rem)] overflow-y-auto my-auto";
  const titleClassName = isParentMode
    ? "text-center text-3xl font-bold text-[#0B2B66] md:text-5xl"
    : "-mt-2 text-3xl md:text-5xl font-bold text-center mb-2";
  const subtitleClassName = isParentMode
    ? "text-center text-base font-medium text-[#5f6576] md:text-lg"
    : "text-center font-medium text-base md:text-xl mb-4 md:mb-6";
  const statusTextClassName = isParentMode
    ? "text-center text-sm font-semibold text-[#9d5229] md:text-base"
    : "text-center text-sm md:text-base mb-4 font-semibold";
  const planGridClassName = isParentMode
    ? "mx-auto mt-8 grid w-full max-w-6xl grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 justify-items-center"
    : "mx-auto grid w-full max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 justify-items-center";

  return (
    <div
      className={outerContainerClassName}
      style={outerContainerStyle}
    >
      {isParentMode && (
        <>
          <div className="absolute inset-x-0 top-0 h-[340px] bg-[radial-gradient(circle_at_top_left,rgba(255,215,180,0.7),transparent_48%),radial-gradient(circle_at_top_right,rgba(255,239,191,0.85),transparent_40%),linear-gradient(180deg,#fdf7f0_0%,#f8f3ea_65%,#f6efe6_100%)]" />
          <div className="absolute -left-20 top-28 h-64 w-64 rounded-full bg-[#f8caa4]/35 blur-3xl" />
          <div className="absolute -right-12 bottom-16 h-72 w-72 rounded-full bg-[#fde8a8]/50 blur-3xl" />
        </>
      )}

      <div className={panelClassName}>
        {(subscriptionStatus === "trialing" || subscriptionStatus === "active") ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`absolute top-3 right-3 flex h-11 w-11 items-center justify-center text-2xl leading-none text-[#0B2B66] transition ${
              isParentMode
                ? "rounded-full border border-[#d8cfbf] bg-white shadow-[0_12px_24px_rgba(126,92,55,0.14)] hover:border-[#08059B]"
                : "rounded-xl border-2 border-[#0B2B66] bg-gradient-to-br from-[#FFE066] via-[#FFB347] to-[#FF7AA2] shadow-[0_4px_0_#0B2B66] hover:brightness-105 active:translate-y-[1px] active:shadow-[0_2px_0_#0B2B66]"
            }`}
            aria-label="Close"
          >
            ×
          </button>
        ) : (
          <button
            type="button"
            onClick={handleLogout}
            className="absolute top-3 right-3 text-xs md:text-sm font-bold px-3 py-1.5 rounded-md border border-[#0B2B66] text-[#0B2B66] bg-white/90 hover:bg-white"
          >
            Logout
          </button>
        )}

        <div className="flex min-h-full flex-col justify-center">
          <div className="mx-auto w-full max-w-6xl">
            {isParentMode && (
              <div className="mb-4 flex justify-center">
                <span className="inline-flex items-center rounded-full border border-[#e5dacb] bg-white/85 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#9d5229]">
                  Parent plans
                </span>
              </div>
            )}

            <h2 className={titleClassName}>
              Free trial for 7 days
            </h2>
            <p className={subtitleClassName}>
              Cancel anytime you want
            </p>

            <div className={isParentMode ? "mb-4 rounded-[28px] border border-[#ece1d3] bg-white/80 p-3 shadow-[0_14px_28px_rgba(140,108,73,0.08)]" : "mb-3 md:mb-4"}>
              <GlobalSubscriptionBanner />
            </div>

            <p className={statusTextClassName}>
              {trialText}
            </p>

            {(isChecking || isPolling) && (
              <p className="text-center text-sm text-blue-700 mb-4">
                Checking latest subscription status...
              </p>
            )}

            {frontendError && (
              <p className="text-center text-sm text-red-700 mb-4">{frontendError}</p>
            )}
          </div>

          <div className={planGridClassName}>
          {planCards.map((plan) => {
            const hasSubscribedPlan = hasCurrentSubscription && Boolean(currentPlanKey);
            const cardActionKey = `${plan.interval}_${plan.planType}`;
            const cardPlanKey = `${plan.planType}_${plan.interval}`;
            const isCurrentPlan = currentPlanKey === cardPlanKey;
            const isCurrentActionLoading =
              (isRedirecting || isCreating) && activeButtonKey === cardActionKey;
            const isButtonDisabled = hasSubscribedPlan
              ? true
              : isRedirecting || isCreating;
            const buttonText = isCurrentActionLoading
              ? "Redirecting..."
              : isCurrentPlan
                ? "Subscribed"
                : "Get Started";
            const buttonClassName = isCurrentPlan
              ? isParentMode
                ? "mt-3 w-full rounded-full border border-[#f0c78b] bg-[linear-gradient(90deg,#fff2bf_0%,#ffd58f_50%,#f8b56d_100%)] px-3 py-3 text-lg font-extrabold tracking-wide text-[#0B2B66] shadow-[0_14px_28px_rgba(240,183,111,0.24)] cursor-not-allowed"
                : "w-full mt-2 font-mulish font-extrabold tracking-wide text-lg py-2.5 px-3 rounded-xl border-2 border-[#0B2B66] text-[#0B2B66] bg-gradient-to-r from-[#FFF27A] via-[#FFBE4D] to-[#FF8F1F] shadow-[0_5px_0_#0B2B66,0_10px_20px_rgba(255,175,64,0.35)] cursor-not-allowed"
              : isParentMode
                ? "mt-3 w-full rounded-full border border-[#d8cfbf] bg-white px-3 py-3 text-lg font-bold text-[#0B2B66] shadow-[0_10px_22px_rgba(140,108,73,0.08)] transition hover:border-[#08059B] hover:text-[#08059B] disabled:opacity-60"
                : "text-primary-dark-blue border w-full mt-2 shadow-sm shadow-primary-dark-blue border-primary-dark-blue font-mulish font-bold text-lg py-2 px-3 rounded-lg hover:bg-black/5 transition disabled:opacity-60";
            const cardClassName = isParentMode
              ? "w-full max-w-[290px] rounded-[28px] border border-[#ebe0d1] bg-[linear-gradient(180deg,#ffffff_0%,#fff8f1_100%)] p-5 text-left shadow-[0_18px_40px_rgba(140,108,73,0.12)] flex min-h-[380px] flex-col"
              : "w-full max-w-[290px] bg-white p-5 rounded-xl shadow-md text-left flex flex-col min-h-[360px]";
            const labelClassName = isParentMode
              ? "mb-3 w-fit rounded-full bg-[#fff0e6] px-4 py-2 text-lg font-bold text-[#9d5229]"
              : "text-lg md:text-xl w-fit px-3 py-2 font-bold bg-[#D9EA49] rounded-md mb-3";

            return (
              <div
                key={plan.name}
                className={cardClassName}
              >
                <h3 className={labelClassName}>
                  {plan.name}
                </h3>
                <p className={`mb-3 text-sm leading-relaxed md:text-base ${isParentMode ? "text-[#5f6576]" : "text-gray-700"}`}>
                  {plan.description}
                </p>
                <ul className={`list-inside list-disc text-left font-medium mb-4 mt-1 text-sm md:text-base space-y-1 ${isParentMode ? "text-[#0B2B66]" : "text-primary-dark-blue"}`}>
                  {plan.benefits.map((benefit) => (
                    <li key={benefit}>{benefit}</li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <p className={`mb-1 text-xs font-semibold md:text-sm ${isParentMode ? "text-[#9d5229]" : "text-gray-600"}`}>
                    Billed in {plan.currency}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className={`mb-3 text-sm font-bold md:text-base ${isParentMode ? "text-[#0B2B66]" : ""}`}>
                      <span className="text-2xl">{plan.price}</span>{" "}
                      {plan.billingText}
                    </p>
                  </div>
                  <button
                    disabled={isButtonDisabled}
                    className={buttonClassName}
                    onClick={() => handleGetStarted(plan.planType, plan.interval)}
                  >
                    {buttonText}
                  </button>
                </div>
              </div>
            );
          })}
          </div>

          <div className="text-center mt-6 md:mt-8">
            <button
              type="button"
              className={`mx-auto mt-2 block text-xs underline md:text-sm ${isParentMode ? "font-bold text-[#5f6576]" : "font-mulish text-gray-500"}`}
              onClick={() => navigate("/subscription-terms")}
            >
              Read Subscription terms
            </button>
          </div>
        </div>
      </div>

      {showLogoutPopup && (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center px-4">
          <div
            className={`relative w-full max-w-md p-6 text-center shadow-xl ${
              isParentMode
                ? "rounded-[28px] border border-[#e5dacb] bg-[linear-gradient(180deg,#ffffff_0%,#fff7ee_100%)]"
                : "rounded-2xl border-2 border-[#0B2B66] bg-white"
            }`}
          >
            <button
              type="button"
              aria-label="Close logout popup"
              onClick={() => setShowLogoutPopup(false)}
              className={`absolute top-4 right-4 flex h-11 w-11 items-center justify-center text-2xl leading-none text-[#0B2B66] transition ${
                isParentMode
                  ? "rounded-full border border-[#d8cfbf] bg-white shadow-[0_12px_24px_rgba(126,92,55,0.14)] hover:border-[#08059B]"
                  : "rounded-xl border-2 border-[#0B2B66] bg-gradient-to-br from-[#FFE066] via-[#FFB347] to-[#FF7AA2] shadow-[0_4px_0_#0B2B66] hover:brightness-105 active:translate-y-[1px] active:shadow-[0_2px_0_#0B2B66]"
              }`}
            >
              &times;
            </button>

            <img
              src={GLOBAL_IMAGES.dingo}
              alt="MathDingo cartoon"
              className="h-12 w-12 object-contain mx-auto mb-4"
            />
            <h3 className="text-2xl font-bold text-[#0B2B66] mb-2">
              Are you sure?
            </h3>
            <p className={`mb-5 font-semibold ${isParentMode ? "text-[#5f6576]" : "text-gray-700"}`}>
              Do you want to logout now?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowLogoutPopup(false)}
                className={`w-full py-2.5 font-bold transition ${
                  isParentMode
                    ? "rounded-full border border-[#d8cfbf] bg-white text-[#0B2B66] hover:border-[#08059B]"
                    : "rounded-lg border-2 border-[#0B2B66] text-[#0B2B66] hover:bg-blue-50"
                }`}
              >
                Stay Here
              </button>
              <button
                type="button"
                onClick={handleLogoutConfirm}
                className={`w-full py-2.5 font-bold transition ${
                  isParentMode
                    ? "rounded-full border border-[#f0c78b] bg-[linear-gradient(90deg,#fff2bf_0%,#ffd58f_50%,#f8b56d_100%)] text-[#0B2B66] shadow-[0_12px_24px_rgba(240,183,111,0.22)] hover:brightness-105"
                    : "rounded-lg border-2 border-[#0B2B66] bg-gradient-to-r from-[#FFF27A] via-[#FFBE4D] to-[#FF8F1F] text-[#0B2B66] shadow-[0_4px_0_#0B2B66] hover:brightness-105"
                }`}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
